import { Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const useStyles = makeStyles(() => ({
  snapWrapper: {
    padding: '48px 0px',
  },

  wrapper: {
    padding: '48px 0px',
  },

  canvasPreview: {
    display: 'none',
  },

  imageCropper: {
    textAlign: 'center',
  },

  buttonWrapper: {
    textAlign: 'center',
  },

  buttonColor: {
    backgroundColor: '#FFFFFF',
  },

  savebuttonLabel: {
    color: '#3F51B5',
  },

  cancelbuttonLabel: {
    color: '#263238',
  },

  cancelButton: {
    marginRight: '24px',
  },

  saveButton: {},
}));

// Increase pixel density for crop preview quality on retina screens.
const pixelRatio = window.devicePixelRatio || 1;

// We resize the canvas down when saving on retina devices otherwise the image
// will be double or triple the preview size.
function getResizedCanvas(canvas, newWidth, newHeight) {
  const tmpCanvas = document.createElement('canvas');
  tmpCanvas.width = newWidth;
  tmpCanvas.height = newHeight;

  const ctx = tmpCanvas.getContext('2d');
  ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, newWidth, newHeight);

  return tmpCanvas;
}

async function generateDownload(previewCanvas, crop) {
  if (!crop || !previewCanvas) {
    return;
  }

  const canvas = getResizedCanvas(previewCanvas, crop.width, crop.height);

  return new Promise(resolve => {
    canvas.toBlob(file => {
      resolve(file);
    }, 'image/jpeg');
  });
}

export function ImageCropper({
  image = '',
  imageName = '',
  onCropDone = () => {},
  onCancel = () => {},
}) {
  const classes = useStyles();

  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const wrapperRef = useRef(null);

  // eslint-disable-next-line no-unused-vars
  const [upImg, setUpImg] = useState();
  const [crop, setCrop] = useState({ unit: '%', width: 50, height: 50, aspect: 0 });
  const [completedCrop, setCompletedCrop] = useState(null);

  const [isMaxPermHeightReached, setIsMaxPermHeightReached] = useState(false);

  const onLoad = useCallback(img => {
    imgRef.current = img;

    const wrapper = wrapperRef.current;
    const wrapperHeight = wrapper.clientHeight;
    const windowInnerHeight = window.innerHeight;

    const offsetHeight = 0;
    const maxPermissibleHeight = windowInnerHeight - offsetHeight;

    if (maxPermissibleHeight <= wrapperHeight) {
      setIsMaxPermHeightReached(true);
    } else {
      setIsMaxPermHeightReached(false);
    }
  }, []);

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );
  }, [completedCrop]);

  const handleSubmit = async () => {
    let data = await generateDownload(previewCanvasRef.current, completedCrop);
    let files = new File([data], imageName, { type: data.type });
    onCropDone(files);
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Grid
      container
      direction="column"
      justify="flex-end"
      className={isMaxPermHeightReached ? classes.snapWrapper : classes.wrapper}
      ref={wrapperRef}
    >
      <Grid item className={classes.imageCropper}>
        <ReactCrop
          src={image}
          onImageLoaded={onLoad}
          crop={crop}
          onChange={c => setCrop(c)}
          onComplete={c => setCompletedCrop(c)}
        />
        <div className={classes.canvasPreview}>
          <canvas
            ref={previewCanvasRef}
            style={{
              width: Math.round(completedCrop?.width ?? 0),
              height: Math.round(completedCrop?.height ?? 0),
            }}
          />
        </div>
      </Grid>

      <br></br>

      <Grid item className={classes.buttonWrapper}>
        <Button
          className={classes.cancelButton}
          classes={{
            containedPrimary: classes.buttonColor,
            label: classes.cancelbuttonLabel,
          }}
          variant="contained"
          disableFocusRipple
          onClick={handleCancel}
        >
          Cancel
        </Button>

        <Button
          className={classes.saveButton}
          classes={{
            containedPrimary: classes.buttonColor,
            label: classes.savebuttonLabel,
          }}
          variant="contained"
          disabled={!completedCrop?.width || !completedCrop?.height}
          onClick={handleSubmit}
        >
          Crop and Save
        </Button>
      </Grid>
    </Grid>
  );
}
